<template>
  <Transition
    name="fade-medium"
    mode="out-in"
    appear
  >
    <div
      v-show="messengerIsVisible"
      class="fixed inset-0 bg-primary-dark opacity-50 z-4000 intercom-window:block"
      @click="onBackgroundOverlayPress"
    ></div>
  </Transition>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import loadExternalScript from '@/utils/loadExternalScript';
import { APIGetIntercomToken } from '@/api';
import { addClass, removeClass } from '@/utils/dom';

const launcherIsVisibleDefault = false;
// Phones (Tailwind `mobile`, <=768px) reach support through "Support chat" in the account menu,
// the floating launcher only covers list content and the Add guest button there
const mobileMediaQuery = window.matchMedia('(max-width: 768px)');

export default {
  mounted() {
    this.bootIntercom();
    mobileMediaQuery.addListener(this.updateLauncherVisibility);
  },
  beforeDestroy() {
    mobileMediaQuery.removeListener(this.updateLauncherVisibility);
    clearTimeout(this.intercomTokenTimer);
  },
  watch: {
    userAuthData: {
      immediate: true,
      handler() {
        this.updateIntercomData();
      },
    },
    userAuthDataId: {
      immediate: false,
      handler(newValue) {
        if (newValue) return;

        const { Intercom } = window;

        if (!Intercom) return;

        // Clearing Intercom messages data if user is signing out
        Intercom('shutdown');
        this.intercomIdentifiedUserId = null;
        this.bootIntercom();
      },
    },
    messengerIsVisible: {
      immediate: true,
      handler(newValue) {
        const { Intercom } = window;

        if (!Intercom) return;

        if (newValue) {
          addClass(document.body, 'overflow-hidden');
        } else {
          removeClass(document.body, 'overflow-hidden');
        }

        if (!newValue) {
          Intercom('hide');
          return;
        }

        Intercom('show');
      },
    },
  },
  computed: {
    ...mapState('intercom', {
      messengerIsVisible: 'messengerIsVisible',
    }),
    ...mapState('user/auth', {
      userAuthData: 'authData',
    }),
    ...mapGetters('user/auth', {
      userAuthDataId: 'authDataId',
    }),
  },
  methods: {
    ...mapActions('intercom', {
      updateMessengerIsVisible: 'updateMessengerIsVisible',
      updateUnreadMessagesCount: 'updateUnreadMessagesCount',
    }),
    bootIntercom(identity) {
      loadExternalScript(`https://widget.intercom.io/widget/${process.env.VUE_APP_INTERCOM_APP_ID}`)
        .then(() => {
          const { Intercom } = window;

          if (!Intercom) return;

          Intercom('boot', this.intercomSettings(identity));

          if (identity) {
            this.intercomIdentifiedUserId = this.userAuthData.id;
            this.scheduleIntercomTokenRefresh(identity.expiresAt);
          } else {
            this.updateIntercomData();
          }

          this.updateMessengerIsVisible(launcherIsVisibleDefault);
          this.attachIntercomListeners();
        })
        .catch((error) => {
          console.error(error);
        });
    },
    intercomSettings(identity) {
      const settings = {
        app_id: process.env.VUE_APP_INTERCOM_APP_ID,
        hide_default_launcher: mobileMediaQuery.matches,
      };

      if (identity) {
        settings.intercom_user_jwt = identity.token;
        settings.name = this.userAuthData.displayName;
      }

      return settings;
    },
    attachIntercomListeners() {
      const { Intercom } = window;

      // Listeners are registered per Messenger session, so they must be set again after every boot
      Intercom('onShow', () => {
        if (this.messengerIsVisible) return;

        this.updateMessengerIsVisible(true);
      });
      Intercom('onHide', () => {
        if (!this.messengerIsVisible) return;

        this.updateMessengerIsVisible(false);
      });
    },
    updateLauncherVisibility() {
      const { Intercom } = window;

      if (!Intercom) return;

      Intercom('update', { hide_default_launcher: mobileMediaQuery.matches });
    },
    async updateIntercomData() {
      const { Intercom } = window;

      if (!Intercom || !this.userAuthData.id) return;

      // Identity verification: user_id and email travel inside a backend-signed JWT (intercom_user_jwt),
      // never as plain attributes, so nobody can open another customer's conversations by guessing an id.
      const userId = this.userAuthData.id;
      if (this.intercomTokenPending) return; // the authData watcher can fire several times per sign-in
      this.intercomTokenPending = true;
      const identity = await APIGetIntercomToken().finally(() => { this.intercomTokenPending = false; });

      if (!window.Intercom || !this.userAuthData.id || this.userAuthData.id !== userId) return;
      if (!identity) return; // backend not configured yet: stay anonymous rather than send an unsigned identity

      if (this.intercomIdentifiedUserId === userId) {
        // Same visitor, new token before the old one expires
        Intercom('update', { intercom_user_jwt: identity.token });
      } else {
        // An anonymous Messenger session cannot be identified with 'update': Intercom answers 403 to the
        // ping. Signing a visitor in means ending that session and booting a new one with the token.
        Intercom('shutdown');
        Intercom('boot', this.intercomSettings(identity));
        this.intercomIdentifiedUserId = userId;
        this.attachIntercomListeners();
      }

      this.scheduleIntercomTokenRefresh(identity.expiresAt);
    },
    scheduleIntercomTokenRefresh(expiresAt) {
      clearTimeout(this.intercomTokenTimer);
      const refreshIn = Math.max(60 * 1000, expiresAt - Date.now() - 5 * 60 * 1000);
      this.intercomTokenTimer = setTimeout(() => this.updateIntercomData(), refreshIn);
    },
    onBackgroundOverlayPress() {
      this.updateMessengerIsVisible(false);
    },
  },
};
</script>

<style>
/* intercom-borderless-frame: Only appears when a message was sent to the user while having the chat closed */

/* Hide launcher by default */
.layout-app .intercom-namespace {
  .intercom-launcher-frame {
    @apply hidden !important;
  }

  .intercom-borderless-frame {
    @apply max-h-250 top-header inset-x-0 w-full bg-gray-200 rounded-lg shadow-12dp !important;
  }

  /* Hide badge count */
  .intercom-launcher-badge-frame {
    @apply hidden
  }
}

/* Base override (Intercom breakpoint at 451px) */
@screen intercom-large {
  .layout-app .intercom-namespace {
    .intercom-messenger-frame {
      @apply h-auto top-20 right-20 bottom-130 left-auto !important;
    }

    .intercom-borderless-frame {
      @apply right-30 left-auto max-h-275 w-auto py-5 px-10 !important;
    }
  }
}
</style>
